import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LoginService from '../../services/LoginService';
import TenantService from "../../services/core/TenantService";
import MeService from "../../services/core/MeService";
import UserPreferencesService from "../../services/core/UserPreferencesService";
import { extractTenantIdFromToken, setTenantId } from '../../services/tenantService';
import { clearDataAreaId, setDataAreaId } from '../../services/dataAreaService';
import i18n from '../../i18n';
import Alert from '../common/Alert';
import LanguageSelector from '../common/LanguageSelector';
import PasswordInputField from '../common/PasswordInputField';
import MfaSetupWizard from '../mfa/MfaSetupWizard';

// ============================================================================
// CONSTANTS
// ============================================================================

const AUTH_TOKEN_KEY = 'authToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
const REMEMBER_ME_KEY = 'rememberMe';
const REMEMBERED_USERNAME_KEY = 'rememberedUsername';
const SUPPORTED_LANGUAGES = new Set(['en', 'es', 'ca', 'eu', 'gl', 'pt', 'pt-BR', 'fr', 'it', 'de', 'jp', 'zh']);

// ============================================================================
// STORAGE HELPERS - Support for "Remember Me"
// ============================================================================

/**
 * Obtener el storage correcto según el tipo de sesión
 * Si rememberMe está activo, usa localStorage (persistente)
 * Si no, usa sessionStorage (se borra al cerrar navegador)
 */
const getStorage = (rememberMe) => {
  return rememberMe ? localStorage : sessionStorage;
};

const normalizeLanguage = (language) => {
  if (!language) return null;
  const raw = String(language).toLowerCase();
  const normalized = raw === 'pt-br' || raw.startsWith('pt-br-')
    ? 'pt-BR'
    : raw.split('-')[0];
  return SUPPORTED_LANGUAGES.has(normalized) ? normalized : null;
};

const applyInitialPreferences = async () => {
  try {
    const preferences = await UserPreferencesService.get({ force: true });

    const preferredLanguage = normalizeLanguage(
      preferences?.lastLanguage || preferences?.defaultLanguage
    );
    if (preferredLanguage) {
      await i18n.changeLanguage(preferredLanguage);
      localStorage.setItem('i18nextLng', preferredLanguage);
    }

    const preferredDataAreaId = preferences?.lastDataAreaId || preferences?.defaultDataAreaId;
    if (preferredDataAreaId) {
      const dataAreaId = String(preferredDataAreaId);
      setDataAreaId(dataAreaId);
      window.dispatchEvent(new CustomEvent('dataAreaChanged', {
        detail: { dataAreaId },
      }));
    }
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.warn("[Login] Could not apply initial user preferences:", error);
    }
  }
};

const Login = () => {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // ============================================================================
  // MFA STEP STATE
  // Deliberately NOT persisted to storage: the challenge token only needs to survive
  // in-memory for this component's lifetime (see architecture doc section 25/26 -
  // don't keep a challenge token around longer than necessary, and losing it on a
  // refresh just sends the user back to step 1, which is safe).
  // ============================================================================
  const [mfaStep, setMfaStep] = useState(false);
  const [challengeToken, setChallengeToken] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [mfaError, setMfaError] = useState(null);
  const [mfaSubmitting, setMfaSubmitting] = useState(false);

  // ============================================================================
  // FORCED MFA SETUP STATE (tenant requires MFA, user has none configured yet)
  // Same "don't persist longer than necessary" rule as the challenge token above.
  // ============================================================================
  const [mfaSetupRequired, setMfaSetupRequired] = useState(false);
  const [setupToken, setSetupToken] = useState(null);

  useEffect(() => {
    // Cargar configuración de rememberMe
    const rememberMeStored = localStorage.getItem(REMEMBER_ME_KEY);
    if (rememberMeStored === 'true') {
      setRememberMe(true);
      
      // Cargar username guardado
      const rememberedUsername = localStorage.getItem(REMEMBERED_USERNAME_KEY);
      if (rememberedUsername) {
        setUsername(rememberedUsername);
      }
    }

    // Redirigir al dashboard si ya está autenticado (en cualquier storage)
    const tokenInLocal = localStorage.getItem(AUTH_TOKEN_KEY);
    const tokenInSession = sessionStorage.getItem(AUTH_TOKEN_KEY);
    if (tokenInLocal || tokenInSession) {
      navigate('/dashboard');
    }
  }, [navigate]);

  /**
   * Shared by the direct (no-MFA) login path and the post-MFA-verification path: everything
   * that happens once we actually hold a valid token pair, so the two paths can never drift
   * apart in what they do after authenticating.
   */
  const completeLoginWithTokens = async (accessToken, refreshToken) => {
      // Extract and store tenantId from JWT token
      const tenantIdFromToken = extractTenantIdFromToken(accessToken);
      if (tenantIdFromToken) {
        setTenantId(tenantIdFromToken);
        if (process.env.NODE_ENV === "development") {
          console.log("[Login] tenantId:", tenantIdFromToken);
        }
      }

      // ============================================================================
      // GUARDAR TOKENS SEGÚN "RECUÉRDAME"
      // ============================================================================
      const storage = getStorage(rememberMe);
      
      // Guardar tokens en el storage correcto
      storage.setItem(AUTH_TOKEN_KEY, accessToken);
      if (refreshToken) {
        storage.setItem(REFRESH_TOKEN_KEY, refreshToken);
      }
      
      // Guardar configuración de rememberMe
      localStorage.setItem(REMEMBER_ME_KEY, String(rememberMe));
      
      // Guardar username si rememberMe está activo
      if (rememberMe) {
        localStorage.setItem(REMEMBERED_USERNAME_KEY, username);
      } else {
        localStorage.removeItem(REMEMBERED_USERNAME_KEY);
      }
      
      localStorage.setItem('displayName', username);
      UserPreferencesService.clearCache();
      clearDataAreaId();

      // Primero cargar /api/me para obtener tenant del usuario
      try {
        const me = await MeService.me();
        console.log('[Login] me response:', me);
        localStorage.setItem('userData', JSON.stringify(me));
        
        // Si es superuser, obtener todos los tenants
        if (me?.superuser) {
          try {
            const tenantsPage = await TenantService.getAll(0, 100, { enabled: true });
            const tenants = tenantsPage?.content || [];
            const first = tenants.find((x) => x.enabled === true) || tenants[0];
            if (first?.id) {
              localStorage.setItem('selectedTenant', String(first.id));
            }
          } catch (e) {
            // Si falla /api/tenants, usar el tenant de /api/me
            if (me?.currentTenantId) {
              localStorage.setItem('selectedTenant', String(me.currentTenantId));
            }
          }
        } else {
          // Usuario normal: usar tenant de /api/me
          if (me?.currentTenantId) {
            console.log('[Login] Setting selectedTenant for non-superuser:', me.currentTenantId);
            localStorage.setItem('selectedTenant', String(me.currentTenantId));
          }
        }
        
        window.dispatchEvent(new Event('userDataChanged'));
        if (me?.displayName) {
          localStorage.setItem('displayName', me.displayName);
        }
        await applyInitialPreferences();
      } catch (e) {
        // No bloquea login
      }

      // Redirigir al dashboard después de iniciar sesión
      navigate('/dashboard');
  };

  const friendlyLoginError = (err) => {
    let userFriendlyMessage = t('login.errors.unableToSignIn');

    const status = err?.response?.status;
    const rawMessage = err?.response?.data?.message || err?.response?.data?.error || err?.response?.raw || err?.message || '';
    const normalizedMessage = rawMessage.toLowerCase();

    const isCredentialIssue =
      normalizedMessage.includes('credential') ||
      normalizedMessage.includes('usuario') ||
      normalizedMessage.includes('user') ||
      normalizedMessage.includes('password') ||
      normalizedMessage.includes('contrase') ||
      normalizedMessage.includes('login') ||
      normalizedMessage.includes('auth');

    if (status === 401 || status === 403 || status === 400 || isCredentialIssue) {
      userFriendlyMessage = t('login.errors.badCredentials');
    } else if (!navigator.onLine) {
      userFriendlyMessage = t('login.errors.noConnection');
    } else if (err?.message && err.message.toLowerCase().includes('network')) {
      userFriendlyMessage = t('login.errors.networkError');
    } else if (status === 500) {
      userFriendlyMessage = t('login.errors.serverError');
    }

    return userFriendlyMessage;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    // Limpiar errores anteriores
    setError(null);
    setFieldErrors({});

    // Validar campos vacíos
    const errors = {};
    if (!username) {
      errors.username = t('validation.usernameRequired');
    }
    if (!password) {
      errors.password = t('validation.passwordRequired');
    } else if (password.length < 3) {
      errors.password = 'Password must be at least 3 characters long.';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setError(t('validation.pleaseFillRequired'));
      return;
    }

    setIsLoading(true);

    try {
      const data = await LoginService.login(username, password);

      if (data?.status === 'MFA_REQUIRED' && data?.challengeToken) {
        // Password already verified server-side; no token issued yet. Switch to step 2
        // in place - no navigation, no reload, nothing written to storage for this step.
        setChallengeToken(data.challengeToken);
        setMfaStep(true);
        setMfaCode('');
        setMfaError(null);
        return;
      }

      if (data?.status === 'MFA_SETUP_REQUIRED' && data?.setupToken) {
        // Password verified, but this user's tenant requires MFA and they have none yet -
        // same "no token issued until MFA is actually satisfied" rule as MFA_REQUIRED.
        setSetupToken(data.setupToken);
        setMfaSetupRequired(true);
        return;
      }

      const accessToken = data?.accessToken;
      const refreshToken = data?.refreshToken;

      if (!accessToken) {
        setError(t('login.errors.badCredentials'));
        return;
      }

      await completeLoginWithTokens(accessToken, refreshToken);
    } catch (err) {
      setError(friendlyLoginError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleMfaSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setMfaError(null);

    // Accept "123456" and "123 456" alike; the backend normalizes too, but trimming here
    // keeps the empty-field check meaningful.
    const normalizedCode = mfaCode.trim();
    if (!normalizedCode) {
      setMfaError(useRecoveryCode ? t('login.mfa.errors.recoveryCodeRequired') : t('login.mfa.errors.codeRequired'));
      return;
    }

    setMfaSubmitting(true);
    try {
      const data = await LoginService.verifyMfa(challengeToken, normalizedCode);
      const accessToken = data?.accessToken;
      const refreshToken = data?.refreshToken;

      if (!accessToken) {
        setMfaError(t('login.mfa.errors.invalidCode'));
        return;
      }

      await completeLoginWithTokens(accessToken, refreshToken);
    } catch (err) {
      const message = (err?.message || '').toUpperCase();
      if (message.includes('MFA_RATE_LIMITED')) {
        setMfaError(t('login.mfa.errors.rateLimited'));
      } else if (message.includes('RECOVERY_CODE')) {
        setMfaError(t('login.mfa.errors.invalidRecoveryCode'));
      } else if (message.includes('CHALLENGE')) {
        // Expired or tampered challenge token: the only way forward is back to step 1.
        setMfaError(t('login.mfa.errors.challengeExpired'));
        setMfaStep(false);
        setChallengeToken(null);
      } else {
        setMfaError(t('login.mfa.errors.invalidCode'));
      }
    } finally {
      setMfaSubmitting(false);
    }
  };

  const backToCredentials = () => {
    setMfaStep(false);
    setChallengeToken(null);
    setMfaCode('');
    setMfaError(null);
    setUseRecoveryCode(false);
  };

  // MfaSetupWizard talks in { codes, generatedAt, ... }; the forced-setup activation endpoint
  // returns { recoveryCodes, generatedAt, accessToken, refreshToken, roles } - normalize the
  // shape here instead of teaching the (otherwise context-agnostic) wizard two response shapes.
  const startForcedMfaSetup = () => LoginService.startForcedMfaSetup(setupToken);
  const activateForcedMfaSetup = async (code) => {
    const result = await LoginService.activateForcedMfaSetup(setupToken, code);
    return {
      codes: result?.recoveryCodes,
      generatedAt: result?.generatedAt,
      accessToken: result?.accessToken,
      refreshToken: result?.refreshToken,
    };
  };

  const handleForcedMfaSetupClose = async (activated, result) => {
    setMfaSetupRequired(false);
    setSetupToken(null);
    if (activated && result?.accessToken) {
      await completeLoginWithTokens(result.accessToken, result.refreshToken);
    }
    // If cancelled, nothing was ever activated server-side - just back to the credentials
    // screen. Logging in again simply re-triggers MFA_SETUP_REQUIRED.
  };

  return (
    <section className="modern-login-page">
      <div className="login-container">
        {/* Language Selector */}
        <div style={{ position: 'absolute', top: '20px', right: '20px', zIndex: 10 }}>
          <LanguageSelector />
        </div>

        <div className="login-card">
          {/* Logo */}
          <div className="login-logo">
            <img
              src={`${process.env.PUBLIC_URL}/img/login-logo.svg`}
              alt="iDynamicsAX"
              className="login-logo-image"
            />
          </div>

          {/* Header */}
          <div className="login-header">
            {mfaStep ? (
              <>
                <h1 className="login-title">{t('login.mfa.title')}</h1>
                <p className="login-subtitle">{t('login.mfa.subtitle')}</p>
              </>
            ) : (
              <>
                <h1 className="login-title">{t('login.title')}</h1>
                <p className="login-subtitle">{t('login.subtitle')}</p>
              </>
            )}
          </div>

          {/* Form */}
          {mfaStep ? (
            <form onSubmit={handleMfaSubmit} className="login-form" aria-label={t('login.mfa.title')}>
              {/* MFA Error Message */}
              <Alert
                type="error"
                message={mfaError}
                onClose={() => setMfaError(null)}
                autoClose={4000}
              />

              {/* MFA Code Field */}
              <div className="login-form-group">
                <label htmlFor="mfaCode">
                  <i className="fa fa-shield"></i>{' '}
                  {useRecoveryCode ? t('login.mfa.recoveryCodeLabel') : t('login.mfa.codeLabel')}
                </label>
                <div className="login-input-wrapper">
                  <input
                    id="mfaCode"
                    name="mfaCode"
                    type="text"
                    inputMode={useRecoveryCode ? 'text' : 'numeric'}
                    autoComplete="one-time-code"
                    autoFocus
                    className="form-control login-input"
                    placeholder={
                      useRecoveryCode
                        ? t('login.mfa.recoveryCodePlaceholder')
                        : t('login.mfa.codePlaceholder')
                    }
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value)}
                    disabled={mfaSubmitting}
                  />
                </div>
              </div>

              {/* Recovery Code Toggle */}
              <div className="login-mfa-toggle">
                <button
                  type="button"
                  className="login-link-button"
                  onClick={() => {
                    setUseRecoveryCode((prev) => !prev);
                    setMfaCode('');
                    setMfaError(null);
                  }}
                  disabled={mfaSubmitting}
                >
                  {useRecoveryCode ? t('login.mfa.useTotpInstead') : t('login.mfa.useRecoveryCode')}
                </button>
              </div>

              {/* Verify Button */}
              <button
                type="submit"
                className="login-button"
                disabled={mfaSubmitting}
              >
                {mfaSubmitting ? (
                  <>
                    <i className="fa fa-spinner fa-spin"></i> {t('login.mfa.verifying')}
                  </>
                ) : (
                  <>
                    <i className="fa fa-check"></i> {t('login.mfa.verify')}
                  </>
                )}
              </button>

              {/* Back to credentials */}
              <div className="login-mfa-back">
                <button
                  type="button"
                  className="login-link-button"
                  onClick={backToCredentials}
                  disabled={mfaSubmitting}
                >
                  <i className="fa fa-arrow-left"></i> {t('login.mfa.backToCredentials')}
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleSubmit} className="login-form">
              {/* Error Message */}
              <Alert
                type="error"
                message={error}
                onClose={() => setError(null)}
                autoClose={3000}
              />

              {/* Username Field */}
              <div className="login-form-group">
                <label htmlFor="username">
                  <i className="fa fa-user"></i> {t('login.username')}
                </label>
                <div className="login-input-wrapper">
                  <input
                    id="username"
                    name="username"
                    type="text"
                    className={`form-control login-input ${fieldErrors.username ? 'has-error' : ''}`}
                    placeholder={t('login.enterUsername')}
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    disabled={isLoading}
                    aria-invalid={!!fieldErrors.username}
                  />
                </div>
                {fieldErrors.username && (
                  <div className="field-error-message">
                    {fieldErrors.username}
                  </div>
                )}
              </div>

              {/* Password Field */}
              <div className="login-form-group">
                <label htmlFor="password">
                  <i className="fa fa-lock"></i> {t('login.password')}
                </label>
                <PasswordInputField
                  id="password"
                  name="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('login.enterPassword')}
                  className={`login-input ${fieldErrors.password ? 'has-error' : ''}`}
                  wrapperClassName="login-input-wrapper"
                  disabled={isLoading}
                  ariaInvalid={!!fieldErrors.password}
                  autoComplete="current-password"
                  toggleLabels={{
                    show: t('common.showPassword'),
                    hide: t('common.hidePassword'),
                  }}
                />
                {fieldErrors.password && (
                  <div className="field-error-message">
                    {fieldErrors.password}
                  </div>
                )}
              </div>

              {/* Remember Me */}
              <div className="login-remember">
                <label className="remember-checkbox">
                  <input
                    id="RememberMe"
                    name="rememberme"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    disabled={isLoading}
                  />
                  <span>{t('login.rememberMe')}</span>
                </label>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="login-button"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <i className="fa fa-spinner fa-spin"></i> {t('login.signingIn')}
                  </>
                ) : (
                  <>
                    <i className="fa fa-sign-in"></i> {t('login.signIn')}
                  </>
                )}
              </button>
            </form>
          )}
        </div>

        {/* Footer */}
        <p className="login-footer">
          {t('common.copyright')}
        </p>
      </div>

      <MfaSetupWizard
        isOpen={mfaSetupRequired}
        onClose={handleForcedMfaSetupClose}
        onSetup={startForcedMfaSetup}
        onActivate={activateForcedMfaSetup}
        introTitle={t('login.mfaSetupRequired.introTitle')}
        introBody={t('login.mfaSetupRequired.introBody')}
      />
    </section>
  );
};

export default Login;
